import {Circle, Group, Line, Rect, Text} from "react-konva";
import {useTableTopSelection} from "@/tabletop/selection/TableTopSelectionHooks.ts";
import {useCombatants} from "@/encounter/combatants/CombatantHooks.ts";
import {useEncounterViewPort} from "@/encounter/viewport/EncounterViewPortHooks.ts";
import {
    getAreaEffectResizeHandles,
    getAreaEffectLockHandlePosition,
    getAreaEffectRotationHandle,
} from "@/tabletop/effects/AreaEffectGeometry.ts";
import {useAreaEffectHandleInteraction} from "@/tabletop/effects/AreaEffectHandleInteractionHooks.ts";
import type {AreaEffect} from "@/encounter/model/AreaEffect.ts";
import type Combatant from "@/encounter/model/Combatant.ts";

const HANDLE_COLORS = {
    length: "#f59e0b",
    width: "#06b6d4",
    radius: "#f59e0b",
};
const DIMENSION_LABELS = {length: "Länge", width: "Breite", radius: "Radius"};

export default function AreaEffectHandleLayer() {
    const {selectedEffect} = useTableTopSelection();
    const {combatants} = useCombatants();
    const {mapTransform} = useEncounterViewPort();

    if (!selectedEffect) return null;
    return <SelectedAreaEffectHandles
        effect={selectedEffect}
        combatant={combatants.find(candidate => candidate.id === selectedEffect.combatantId)}
        scale={mapTransform.scale}
    />;
}

function SelectedAreaEffectHandles({effect, combatant, scale}: {
    effect: AreaEffect;
    combatant?: Combatant;
    scale: number;
}) {
    const origin = combatant?.position ?? effect.position;
    const rotation = combatant?.rotation ?? effect.rotation ?? 0;
    const {startResize, startRotation, toggleLock} = useAreaEffectHandleInteraction(effect, origin, rotation);
    const dimensionHandles = getAreaEffectResizeHandles(effect);
    const resizeHandles = effect.locked ? [] : dimensionHandles;
    const rotationHandle = effect.locked || combatant
        ? undefined
        : getAreaEffectRotationHandle(effect, 32 / scale);
    const lockHandlePosition = getAreaEffectLockHandlePosition(effect);

    return (
        <Group x={origin.x} y={origin.y} rotation={rotation}>
            {dimensionHandles.map(handle => {
                const value = effect[handle.dimension];
                if (value === undefined) return null;
                const label = `${DIMENSION_LABELS[handle.dimension]} ${value} m`;
                const labelWidth = label.length * 6.5 + 12;
                return <Group key={`${handle.dimension}-label`} x={handle.position.x} y={handle.position.y}
                    rotation={-rotation} scaleX={1 / scale} scaleY={1 / scale} listening={false}>
                    <Rect x={-labelWidth / 2} y={12} width={labelWidth} height={20}
                        cornerRadius={5} fill="rgba(3, 7, 18, 0.85)" stroke={effect.color}/>
                    <Text x={-labelWidth / 2} y={15} width={labelWidth} align="center"
                        text={label} fontSize={12} fill="white"/>
                </Group>;
            })}
            {resizeHandles.map(handle => (
                <Circle
                    key={handle.dimension}
                    x={handle.position.x}
                    y={handle.position.y}
                    radius={7 / scale}
                    fill="white"
                    stroke={HANDLE_COLORS[handle.dimension]}
                    strokeWidth={3 / scale}
                    shadowColor="black"
                    shadowBlur={3 / scale}
                    onMouseDown={event => startResize(handle.dimension, event)}
                />
            ))}
            {rotationHandle && <>
                <Line
                    points={[
                        rotationHandle.connectorStart.x,
                        rotationHandle.connectorStart.y,
                        rotationHandle.position.x,
                        rotationHandle.position.y,
                    ]}
                    stroke="#a855f7"
                    strokeWidth={2 / scale}
                    dash={[5 / scale, 4 / scale]}
                    listening={false}
                />
                <Circle
                    x={rotationHandle.position.x}
                    y={rotationHandle.position.y}
                    radius={7 / scale}
                    fill="white"
                    stroke="#a855f7"
                    strokeWidth={3 / scale}
                    shadowColor="black"
                    shadowBlur={3 / scale}
                    onMouseDown={startRotation}
                />
            </>}
            <Group
                x={lockHandlePosition.x}
                y={lockHandlePosition.y}
                rotation={-rotation}
                scaleX={1 / scale}
                scaleY={1 / scale}
                onMouseDown={toggleLock}
            >
                <Circle
                    radius={11}
                    fill={effect.locked ? "#7c3aed" : "white"}
                    stroke="#7c3aed"
                    strokeWidth={2}
                    shadowColor="black"
                    shadowBlur={3}
                />
                <Line
                    points={effect.locked
                        ? [-4, -1, -4, -5, 4, -5, 4, -1]
                        : [-4, -1, -4, -5, 4, -5, 4, -2]}
                    stroke={effect.locked ? "white" : "#7c3aed"}
                    strokeWidth={2}
                    lineCap="round"
                    lineJoin="round"
                    listening={false}
                />
                <Rect
                    x={-5}
                    y={-1}
                    width={10}
                    height={8}
                    cornerRadius={1}
                    fill={effect.locked ? "white" : "#7c3aed"}
                    listening={false}
                />
            </Group>
        </Group>
    );
}
